import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { fetchAdminStats, fetchAiUsage } from '../lib/api';
import type { AdminAiUsageGroup, AdminStatsResponse } from '../types/api';

function dateDaysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}

function nextDate(date: string): string {
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + 1);
  return value.toISOString().slice(0, 10);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}

function formatCost(value: number): string {
  return `$${value.toFixed(2)}`;
}

function share(value: number, total: number): string {
  return total === 0 ? '—' : `${((value / total) * 100).toFixed(1)}%`;
}

function featureLabel(feature: string | null): string {
  if (feature === 'chat') return 'Chat';
  if (feature === 'digest') return 'Digest summary';
  return feature || 'Unknown';
}

function encryptionStateLabel(state: string): string {
  switch (state) {
    case 'encrypted_sync_enabled':
      return 'Encrypted sync enabled';
    case 'bootstrap_pending':
      return 'Bootstrap pending';
    case 'rotation_required':
      return 'Rotation required';
    case 'suspended':
      return 'Suspended';
    default:
      return state;
  }
}

function entityTypeLabel(entityType: string): string {
  switch (entityType) {
    case 'event':
      return 'Event';
    case 'active_event_session':
      return 'Active event session';
    default:
      return entityType;
  }
}

function featureGroup(groups: AdminAiUsageGroup[], feature: string): AdminAiUsageGroup {
  return (
    groups.find((group) => group.group_value === feature) ?? {
      group_value: feature,
      calls: 0,
      failed_calls: 0,
      unknown_usage_calls: 0,
      input_tokens: 0,
      output_tokens: 0,
      estimated_cost_usd: 0,
    }
  );
}

function StatCard({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="rounded-lg bg-white p-5 shadow">
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-2 text-3xl font-semibold tracking-tight text-gray-900">{value}</p>
      {detail ? <p className="mt-1 text-sm text-gray-500">{detail}</p> : null}
    </div>
  );
}

function DictationSection({ data }: { data: AdminStatsResponse['dictation'] }) {
  const duration = (ms: number | null) => ms === null ? '—' : `${(ms / 1000).toFixed(1)} s`;
  const textParse = data.textParse;
  return (
    <>
      <SectionTitle
        title="Dictation"
        description="WebSocket audio sessions and text parse API calls in the selected date range."
      />
      <p className="mt-4 text-sm font-medium text-gray-700">WebSocket audio</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Average dictation duration" value={duration(data.averageSessionDurationMs)}
          detail={`${formatNumber(data.measuredSessions)} measured sessions`} />
        <StatCard label="WebSocket sessions" value={formatNumber(data.sessions)}
          detail="Each v1 connection or resumable v2 session" />
        <StatCard label="Total audio" value={`${(data.totalAudioMs / 60000).toFixed(1)} min`}
          detail={`${formatNumber(data.measuredChunks)} measured chunks`} />
        <StatCard label="Average audio chunk" value={duration(data.averageChunkDurationMs)} />
      </div>
      {data.sessions === 0 ? (
        <p className="mt-3 text-sm text-gray-500">No dictation audio recorded in this date range. Statistics start when duration tracking is enabled.</p>
      ) : data.unmeasuredChunks > 0 ? (
        <p className="mt-3 text-sm text-gray-500">
          {formatNumber(data.unmeasuredChunks)} chunks could not be measured. Sessions containing these chunks are excluded from the session average.
        </p>
      ) : null}
      <p className="mt-3 text-xs text-gray-500">Audio is grouped by session start date, including sessions ended early. Connection time and transcription latency are excluded.</p>
      {textParse ? (
        <section aria-labelledby="text-parse-title" className="mt-6 overflow-hidden rounded-lg bg-white shadow">
          <div className="border-b border-gray-200 px-5 py-4">
            <h4 id="text-parse-title" className="text-base font-semibold text-gray-900">Text parse API</h4>
            <p className="mt-1 text-sm text-gray-500">Combined v1 and v2 transcript parsing, grouped by request start date.</p>
          </div>
          <dl className="grid grid-cols-2 gap-6 p-5 xl:grid-cols-4">
            {[
              ['Parse requests', formatNumber(textParse.requests)],
              ['API success', textParse.successRate === null ? '—' : `${(textParse.successRate * 100).toFixed(1)}%`],
              ['Avg. API time', duration(textParse.averageDurationMs)],
              ['Events returned', formatNumber(textParse.eventsReturned)],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-sm font-medium text-gray-500">{label}</dt>
                <dd className="mt-2 text-2xl font-semibold tracking-tight text-gray-900">{value}</dd>
              </div>
            ))}
          </dl>
          <p className="border-t border-gray-200 px-5 py-3 text-xs text-gray-500">
            {textParse.requests === 0
              ? 'No text parse requests recorded in this date range. Tracking starts when enabled.'
              : `${formatNumber(textParse.failedRequests)} failed requests. Average API time covers successful requests only; events returned are proposals, not saved events.`}
            {' '}Counts include validated requests only; retries count separately. Audio duration is unavailable for text-only input.
          </p>
        </section>
      ) : null}
    </>
  );
}

function MonitorSection({ data }: { data: AdminStatsResponse['monitor'] }) {
  const duration = (ms: number | null) =>
    ms === null ? '—' : `${(ms / 60000).toFixed(1)} min`;

  return (
    <>
      <SectionTitle
        title="Baby monitor"
        description="Monitor session activity in the selected date range, grouped by session start date."
      />
      <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Sessions started" value={formatNumber(data.sessionsStarted)} />
        <StatCard
          label="Sessions answered"
          value={formatNumber(data.sessionsAnswered)}
          detail="An answer does not confirm an actual connection."
        />
        <StatCard
          label="Explicitly closed"
          value={formatNumber(data.explicitlyClosed)}
          detail={`${formatNumber(data.expired)} expired · ${formatNumber(data.active)} active`}
        />
        <StatCard
          label="Average estimated duration"
          value={duration(data.averageEstimatedDurationMs)}
          detail={`${formatNumber(data.durationSessions)} completed sessions`}
        />
        <StatCard
          label="Total estimated duration"
          value={duration(data.totalEstimatedDurationMs)}
          detail="From first answer to explicit close"
        />
        <StatCard
          label="Excluded from duration"
          value={formatNumber(data.excludedDurationSessions)}
          detail="Incomplete sessions are excluded."
        />
      </div>
      {data.sessionsStarted === 0 ? (
        <p className="mt-3 text-sm text-gray-500">
          No monitor sessions recorded in this date range. Statistics start when monitor tracking is enabled.
        </p>
      ) : null}
      <p className="mt-3 text-xs text-gray-500">
        Estimated duration runs from the first answer to explicit close. Expired, active, and otherwise incomplete sessions are excluded from duration averages.
      </p>
    </>
  );
}

function SectionTitle({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="mt-10">
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-500">{description}</p>
    </div>
  );
}

function PlusFamilySection({ data }: { data: AdminStatsResponse }) {
  const { current, inWindow } = data.families;
  const eventRow = data.syncedRecords.current.byEntityType.find(
    (row) => row.entityType === 'event',
  );
  const entityRows = mergedEntityRows(data);
  const syncEnabled =
    current.byEncryptionState.find((row) => row.state === 'encrypted_sync_enabled')
      ?.count ?? 0;

  return (
    <>
      <SectionTitle
        title="Plus families"
        description="Current E2EE households. Created and deleted counts use the selected date range."
      />
      <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Current families"
          value={formatNumber(current.total)}
          detail={`${formatNumber(syncEnabled)} sync-enabled · ${formatNumber(current.activeMembers)} active members`}
        />
        <StatCard
          label="Shared families"
          value={formatNumber(current.shared)}
          detail={`${formatNumber(current.solo)} solo`}
        />
        <StatCard
          label="Created in range"
          value={formatNumber(inWindow.created)}
        />
        <StatCard
          label="Deleted in range"
          value={formatNumber(inWindow.deleted)}
        />
      </div>

      <div className="mt-6 overflow-hidden rounded-lg bg-white shadow">
        <div className="border-b border-gray-200 px-6 py-4">
          <h4 className="text-base font-semibold text-gray-900">By encryption state</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                {['State', 'Families', 'Share'].map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {current.byEncryptionState.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-sm text-gray-500">
                    No Plus families yet.
                  </td>
                </tr>
              ) : (
                current.byEncryptionState.map((row) => (
                  <tr key={row.state}>
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                      {encryptionStateLabel(row.state)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                      {formatNumber(row.count)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                      {share(row.count, current.total)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <SectionTitle
        title="Synced records"
        description="Current ciphertext record winners (one row per synced item) and mutations accepted in the selected date range."
      />
      <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Live items"
          value={formatNumber(data.syncedRecords.current.live)}
          detail={`${formatNumber(data.syncedRecords.current.deleted)} deleted`}
        />
        <StatCard
          label="Live events"
          value={formatNumber(eventRow?.live ?? 0)}
          detail={`${formatNumber(eventRow?.deleted ?? 0)} deleted events`}
        />
        <StatCard
          label="Mutations in range"
          value={formatNumber(data.syncedRecords.inWindow.mutations)}
        />
      </div>

      <div className="mt-6 overflow-hidden rounded-lg bg-white shadow">
        <div className="border-b border-gray-200 px-6 py-4">
          <h4 className="text-base font-semibold text-gray-900">By entity type</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                {['Entity', 'Live', 'Deleted', 'Mutations in range'].map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {entityRows.map((row) => (
                <tr key={row.entityType}>
                  <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                    {entityTypeLabel(row.entityType)}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                    {formatNumber(row.live)}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                    {formatNumber(row.deleted)}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                    {formatNumber(row.mutations)}
                  </td>
                </tr>
              ))}
              {entityRows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-sm text-gray-500">
                    No synced records yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <p className="border-t border-gray-200 px-6 py-3 text-xs text-gray-500">{data.note}</p>
      </div>
    </>
  );
}

function mergedEntityRows(data: AdminStatsResponse) {
  const rows = new Map<
    string,
    { entityType: string; live: number; deleted: number; mutations: number }
  >();
  for (const row of data.syncedRecords.current.byEntityType) {
    rows.set(row.entityType, {
      entityType: row.entityType,
      live: row.live,
      deleted: row.deleted,
      mutations: 0,
    });
  }
  for (const row of data.syncedRecords.inWindow.byEntityType) {
    const existing = rows.get(row.entityType);
    if (existing) {
      existing.mutations = row.mutations;
    } else {
      rows.set(row.entityType, {
        entityType: row.entityType,
        live: 0,
        deleted: 0,
        mutations: row.mutations,
      });
    }
  }
  return [...rows.values()];
}

export default function StatisticsPage() {
  const [from, setFrom] = useState(() => dateDaysAgo(30));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [appliedRange, setAppliedRange] = useState({ from, to });
  const toExclusive = nextDate(appliedRange.to);
  const statsQuery = useQuery({
    queryKey: ['admin-stats', appliedRange],
    queryFn: () => fetchAdminStats(appliedRange.from, toExclusive),
  });
  const aiQuery = useQuery({
    queryKey: ['admin-ai-usage', appliedRange],
    queryFn: () => fetchAiUsage(appliedRange.from, toExclusive),
  });

  const chat = featureGroup(aiQuery.data?.groups ?? [], 'chat');
  const digest = featureGroup(aiQuery.data?.groups ?? [], 'digest');
  const totalTokens =
    (aiQuery.data?.totals.inputTokens ?? 0) + (aiQuery.data?.totals.outputTokens ?? 0);

  const applyRange = () => {
    if (!from || !to || from > to) return;
    setAppliedRange({ from, to });
  };

  const refresh = () => {
    void statsQuery.refetch();
    void aiQuery.refetch();
  };

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:tracking-tight">
            Statistics
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            Plus families, synced records, dictation, and cloud AI usage
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm text-gray-600">
            From
            <input
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              className="mt-1 block rounded-md border border-gray-300 px-2 py-2 text-sm shadow-sm"
            />
          </label>
          <label className="text-sm text-gray-600">
            To
            <input
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              className="mt-1 block rounded-md border border-gray-300 px-2 py-2 text-sm shadow-sm"
            />
          </label>
          <button
            type="button"
            onClick={applyRange}
            disabled={!from || !to || from > to}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={refresh}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {statsQuery.isLoading ? (
        <div className="mt-6 rounded-lg bg-white py-16 text-center shadow">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900" />
          <p className="mt-2 text-sm text-gray-500">Loading statistics...</p>
        </div>
      ) : statsQuery.error ? (
        <div className="mt-6 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">
            Error loading statistics: {statsQuery.error.message}
          </p>
        </div>
      ) : statsQuery.data ? (
        <>
          <PlusFamilySection data={statsQuery.data} />
          {statsQuery.data.dictation ? <DictationSection data={statsQuery.data.dictation} /> : null}
          {statsQuery.data.monitor ? <MonitorSection data={statsQuery.data.monitor} /> : null}
        </>
      ) : null}

      <SectionTitle
        title="AI usage"
        description="Compare cloud chat and digest summary usage in the selected period."
      />

      {aiQuery.isLoading ? (
        <div className="mt-4 rounded-lg bg-white py-16 text-center shadow">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900" />
          <p className="mt-2 text-sm text-gray-500">Loading AI usage...</p>
        </div>
      ) : aiQuery.error ? (
        <div className="mt-4 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">Error loading AI usage: {aiQuery.error.message}</p>
        </div>
      ) : aiQuery.data ? (
        <>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Total calls" value={formatNumber(aiQuery.data.totals.calls)} />
            <StatCard
              label="Chat share"
              value={share(chat.calls, aiQuery.data.totals.calls)}
              detail={`${formatNumber(chat.calls)} provider calls`}
            />
            <StatCard
              label="Digest share"
              value={share(digest.calls, aiQuery.data.totals.calls)}
              detail={`${formatNumber(digest.calls)} provider calls`}
            />
            <StatCard
              label="Estimated cost"
              value={formatCost(aiQuery.data.totals.estimatedCostUsd)}
              detail={`${formatNumber(totalTokens)} input/output tokens`}
            />
          </div>

          <div className="mt-6 overflow-hidden rounded-lg bg-white shadow">
            <div className="border-b border-gray-200 px-6 py-4">
              <h4 className="text-base font-semibold text-gray-900">Usage by feature</h4>
              <p className="mt-1 text-sm text-gray-500">
                Percentages are based on provider calls in the selected period.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    {['Feature', 'Calls', 'Share', 'Input tokens', 'Output tokens', 'Failed', 'Est. cost'].map(
                      (heading) => (
                        <th
                          key={heading}
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                        >
                          {heading}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {aiQuery.data.groups.map((group) => (
                    <tr key={group.group_value ?? 'unknown'}>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                        {featureLabel(group.group_value)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                        {formatNumber(group.calls)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                        {share(group.calls, aiQuery.data.totals.calls)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                        {formatNumber(group.input_tokens)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                        {formatNumber(group.output_tokens)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                        {formatNumber(group.failed_calls)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                        {formatCost(group.estimated_cost_usd)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="border-t border-gray-200 px-6 py-3 text-xs text-gray-500">{aiQuery.data.note}</p>
          </div>
        </>
      ) : null}
    </div>
  );
}
